import { HttpContext, HttpContextToken, HttpEventType, HttpInterceptorFn } from '@angular/common/http';
import { tap } from 'rxjs';
import { estCoupureReseau } from '../../../core/services/network-status.service';

/**
 * Socle du téléversement GED : ce que partagent TOUS les points de dépôt (panneau documentaire,
 * PV de séance, dépôt direct au dossier permanent). Transport avec progression réelle, taille
 * maximale, famille de fichier, formatage — décidés une fois, ici.
 */

/**
 * Taille maximale d'une pièce — miroir de `spring.servlet.multipart.max-file-size` (application.yml,
 * `GED_MAX_FILE_SIZE`, 50 Mo par défaut).
 *
 * <p>Vérifiée AVANT l'envoi : sans elle, l'agent attendrait la fin du transfert d'un fichier de
 * 200 Mo pour apprendre que le serveur le refuse (413). Le serveur reste le seul juge ; ce contrôle
 * ne fait qu'épargner l'attente.
 */
export const GED_TAILLE_MAX = 50 * 1024 * 1024;

/** Reçoit l'avancement d'un envoi : octets partis, et total quand le navigateur le connaît. */
export type UploadProgressSink = (envoye: number, total: number | null) => void;

/**
 * Jeton porté par une requête qui veut suivre son envoi.
 *
 * <p>Pourquoi un jeton plutôt que `observe: 'events'` : les adapters {@link DocumentsSource} de dix
 * modules renvoient déjà `Observable<DocumentView>`. Le jeton leur laisse cette forme — ils n'ont
 * qu'à transmettre le `context` reçu — et l'intercepteur ci-dessous fait le reste.
 */
export const GED_UPLOAD_PROGRESS = new HttpContextToken<UploadProgressSink | null>(() => null);

/** Le contexte HTTP d'un envoi suivi. */
export function contexteEnvoi(sink: UploadProgressSink): HttpContext {
  return new HttpContext().set(GED_UPLOAD_PROGRESS, sink);
}

/**
 * Active `reportProgress` sur les requêtes qui portent {@link GED_UPLOAD_PROGRESS} et remonte les
 * événements d'envoi au suiveur. Les appelants en `observe: 'body'` ne voient rien changer :
 * HttpClient ne leur livre que la réponse.
 *
 * <p>Placé EN TÊTE de chaîne : le rejeu après renouvellement de session (errorInterceptor, en aval)
 * repart de la requête déjà clonée, et ses événements repassent par ce `tap`.
 *
 * <p>⚠️ Suppose le backend XHR (pas de `withFetch()`) : `fetch` ne publie aucune progression d'envoi.
 */
export const uploadProgressInterceptor: HttpInterceptorFn = (req, next) => {
  const sink = req.context.get(GED_UPLOAD_PROGRESS);
  if (!sink) {
    return next(req);
  }
  return next(req.clone({ reportProgress: true })).pipe(
    tap((ev) => {
      if (ev.type === HttpEventType.UploadProgress) {
        sink(ev.loaded, ev.total ?? null);
      }
    }),
  );
};

// ---------------------------------------------------------------------------------------------
// Famille de fichier : l'icône, la couleur et la possibilité d'un aperçu en dépendent.
// ---------------------------------------------------------------------------------------------

export type GedFileKind = 'pdf' | 'image' | 'sheet' | 'doc' | 'slides' | 'archive' | 'text' | 'other';

/** L'extension d'un nom de fichier, en minuscules, sans le point (`''` s'il n'y en a pas). */
export function extensionDe(nom: string): string {
  const i = nom.lastIndexOf('.');
  return i > 0 && i < nom.length - 1 ? nom.substring(i + 1).toLowerCase() : '';
}

/**
 * La famille d'un fichier, d'après son type MIME — et son extension quand le navigateur ne donne
 * pas de type (fréquent pour `.docx` / `.xlsx` sous certains postes Windows).
 */
export function familleDe(mime: string | null | undefined, nom: string): GedFileKind {
  const m = (mime ?? '').toLowerCase();
  const ext = extensionDe(nom);
  if (m === 'application/pdf' || ext === 'pdf') {
    return 'pdf';
  }
  if (m.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'tif', 'tiff'].includes(ext)) {
    return 'image';
  }
  if (m.includes('spreadsheet') || m.includes('excel') || m === 'text/csv' || ['xls', 'xlsx', 'ods', 'csv'].includes(ext)) {
    return 'sheet';
  }
  if (m.includes('presentation') || m.includes('powerpoint') || ['ppt', 'pptx', 'odp'].includes(ext)) {
    return 'slides';
  }
  if (m.includes('wordprocessing') || m.includes('msword') || m.includes('opendocument.text') || ['doc', 'docx', 'odt', 'rtf'].includes(ext)) {
    return 'doc';
  }
  if (m.includes('zip') || m.includes('compressed') || m.includes('x-7z') || m.includes('x-rar') || ['zip', 'rar', '7z', 'gz', 'tar'].includes(ext)) {
    return 'archive';
  }
  if (m.startsWith('text/') || ['txt', 'md', 'xml', 'json'].includes(ext)) {
    return 'text';
  }
  return 'other';
}

/** Icône Lucide de chaque famille (toutes fournies dans `app.config.ts`). */
export const ICONE_FAMILLE: Record<GedFileKind, string> = {
  pdf: 'file-text',
  image: 'file-image',
  sheet: 'file-spreadsheet',
  doc: 'file-text',
  slides: 'file',
  archive: 'file-archive',
  text: 'file-text',
  other: 'file',
};

/**
 * Le navigateur sait-il montrer ce fichier sans rien installer ? PDF (visionneuse intégrée), images
 * matricielles et texte brut. Un `.docx` ne s'affiche pas : on le dit plutôt que d'ouvrir un onglet
 * qui déclenche un téléchargement surprise.
 *
 * <p>Le SVG est exclu À DESSEIN : servi en `blob:` même origine, il exécuterait ses scripts.
 */
export function apercuPossible(mime: string | null | undefined, nom: string): boolean {
  const k = familleDe(mime, nom);
  if (k === 'image') {
    return extensionDe(nom) !== 'svg' && (mime ?? '').toLowerCase() !== 'image/svg+xml';
  }
  return k === 'pdf' || k === 'text';
}

/**
 * Type MIME à imposer au Blob pour l'aperçu. Le serveur renvoie parfois `application/octet-stream`,
 * que le navigateur télécharge au lieu d'afficher : on repart du type connu, sinon de l'extension.
 */
export function mimePourApercu(mime: string | null | undefined, nom: string): string {
  const m = (mime ?? '').toLowerCase();
  if (m && m !== 'application/octet-stream') {
    return m;
  }
  const ext = extensionDe(nom);
  const parExt: Record<string, string> = {
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    bmp: 'image/bmp',
    txt: 'text/plain',
    csv: 'text/plain',
    md: 'text/plain',
  };
  return parExt[ext] ?? 'application/octet-stream';
}

/**
 * Taille lisible. Unités latines (`Ko`, `Mo`) dans les deux langues, comme partout ailleurs dans le
 * SI : c'est une valeur technique, affichée en `dir="ltr"`.
 */
export function tailleLisible(octets: number): string {
  if (octets < 1024) {
    return `${octets} o`;
  }
  if (octets < 1024 * 1024) {
    return `${Math.round(octets / 1024)} Ko`;
  }
  return `${(octets / (1024 * 1024)).toFixed(1)} Mo`;
}

/**
 * Isole une valeur technique (taille, nom de fichier) insérée dans une phrase : sans cela, dans une
 * phrase arabe, « 50.0 Mo » se lit « Mo 50.0 » — l'algorithme bidi réordonne les morceaux latins.
 * LRI … PDI (U+2066 / U+2069) : un îlot gauche-à-droite, sans effet sur le reste de la ligne.
 */
export function isoler(v: string): string {
  return `\u2066${v}\u2069`;
}

/**
 * Le motif d'un échec d'envoi, en toutes lettres. Le serveur sait POURQUOI il refuse — le taire
 * obligerait l'agent à deviner, et « échec » seul ferait croire à une panne.
 */
export function motifEchecEnvoi(e: unknown, ar: boolean): string {
  if (estCoupureReseau(e)) {
    return ar ? 'انقطع الاتصال أثناء الرفع.' : 'Connexion perdue pendant l’envoi.';
  }
  const err = e as { status?: number; error?: { detail?: string } } | null;
  if (err?.status === 413) {
    return ar
      ? `الملف يتجاوز الحجم الأقصى المسموح به (${isoler(tailleLisible(GED_TAILLE_MAX))}).`
      : `Le fichier dépasse la taille maximale autorisée (${tailleLisible(GED_TAILLE_MAX)}).`;
  }
  // 403 seulement : un 404 est la réponse anti-énumération de l'API, il ne dit pas « interdit ».
  if (err?.status === 403) {
    return ar ? 'لا تملك صلاحية الإيداع في هذا الموضع.' : 'Vous n’avez pas le droit de déposer à cet emplacement.';
  }
  if (err?.error?.detail) {
    return err.error.detail;
  }
  return ar ? 'تعذّر رفع الملف.' : 'Échec du téléversement.';
}
